<script setup lang="ts">
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import AuthLayout from '../components/AuthLayout.vue';
import { Button, Input } from '../components/ui';
import { submitNewPassword } from '../api';
import { MIN_PASSWORD_LENGTH } from '../constants';
import { isValidPassword } from '../services/validators';

const instance = getCurrentInstance();
const route = instance?.proxy?.$route;

const password = ref('');
const passwordConfirmation = ref('');
const passwordError = ref('');
const loading = ref(false);
const success = ref(false);
const token = ref('');

onMounted(() => {
  const rawToken = route?.query.reset_password_token;
  if (typeof rawToken === 'string') {
    token.value = rawToken;
  }
});

const topLinks = computed(() => {
  if (success.value) {
    return [{ url: '/users/sign_in', text: 'Back to Login' }];
  }
  return [
    { url: '/users/sign_in', text: 'Sign In' },
    { url: '/users/confirmation/new', text: "Didn't receive confirmation instructions?" },
    { url: '/users/unlock/new', text: "Didn't receive unlock instructions?" },
  ];
});

const bottomLinks = computed(() => []);

async function handleSubmit() {
  passwordError.value = '';

  if (password.value !== passwordConfirmation.value) {
    passwordError.value = 'Passwords do not match.';
    return;
  }

  if (!isValidPassword(password.value, MIN_PASSWORD_LENGTH)) {
    passwordError.value = `Enter a valid password at least ${MIN_PASSWORD_LENGTH} characters long.`;
    return;
  }

  if (!token.value) {
    passwordError.value = 'Invalid token.';
    return;
  }

  loading.value = true;

  try {
    await submitNewPassword(password.value, passwordConfirmation.value, token.value);
    success.value = true;
  } catch (error) {
    passwordError.value = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <AuthLayout title="Change your password" :top-links="topLinks" :bottom-links="bottomLinks">
    <div v-if="success" class="text-white/80 text-base leading-relaxed">
      Please use your new password to authenticate.
    </div>

    <form v-else @submit.prevent="handleSubmit">
      <Input
        v-model="password"
        type="password"
        placeholder="New password"
        autocomplete="new-password"
        :error="passwordError"
      />

      <Input
        v-model="passwordConfirmation"
        type="password"
        placeholder="Password confirmation"
        autocomplete="new-password"
      />

      <div class="mt-form-gap">
        <Button type="submit" variant="primary" size="lg" :loading="loading" :disabled="loading">
          Change my password
        </Button>
      </div>
    </form>
  </AuthLayout>
</template>
